import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import { AuthGuard } from 'src/guards/AuthGuard';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // { element: <Navigate to="/dashboard" replace />, index: true },
        { element: <Navigate to="/course/list" replace />, index: true },
        { path: 'dashboard', element: <Home /> },
        {
          path: 'course',
          children: [
            { element: <Navigate to="/course/list" replace />, index: true },
            { path: 'list', element: <CoursesList /> },
            { path: 'my-courses', element: <PageOne /> },
            { path: 'saved-courses', element: <PageOne /> },
            { path: 'create', element: <CreateCourse /> },
            { path: 'edit', element: <CreateCourse /> },
            { path: 'participant', element: <ParticipantsList /> },
            { path: 'leader-board', element: <LeaderBoardList /> },
          ],
        },
        {
          path: 'tag',
          children: [
            { element: <Navigate to="/tag/list" replace />, index: true },
            { path: 'list', element: <TagsList /> },
            { path: 'create', element: <TagsCreate /> },
            { path: 'edit/:tagID', element: <TagsEdit /> },
          ],
        },
        {
          path: 'course-level',
          children: [
            { element: <Navigate to="/course-level/list" replace />, index: true },
            { path: 'list', element: <ZoneList /> },
            { path: 'create', element: <ZoneCreate /> },
            { path: 'edit/:courseLevelID', element: <ZoneEdit /> },
          ],
        },
        {
          path: 'trainer',
          children: [
            { element: <Navigate to="/trainer/list" replace />, index: true },
            { path: 'list', element: <TrainerList /> },
            { path: 'create', element: <TrainerCreate /> },
            { path: 'edit/:trainerID', element: <TrainerEdit /> },
          ],
        },
        {
          path: 'faq',
          children: [
            { element: <Navigate to="/faq/list" replace />, index: true },
            { path: 'list', element: <FaqList /> },
            { path: 'create', element: <FaqCreate /> },
            { path: 'edit/:faqID', element: <FaqEdit /> },
          ],
        },
        {
          path: 'slider',
          children: [
            { element: <Navigate to="/slider/list" replace />, index: true },
            { path: 'list', element: <SliderList /> },
            { path: 'create', element: <SliderCreate /> },
            { path: 'edit/:sliderID', element: <SliderEdit /> },
          ],
        },
        {
          path: 'story',
          children: [
            { element: <Navigate to="/story/list" replace />, index: true },
            { path: 'list', element: <StoryList /> },
            { path: 'create', element: <StoryCreate /> },
            { path: 'edit/:storyID', element: <StoryEdit /> },
          ],
        },
        {
          path: 'content-type',
          children: [
            { element: <Navigate to="/content-type/list" replace />, index: true },
            { path: 'list', element: <ContentTypeList /> },
            { path: 'create', element: <ContentTypeCreate /> },
            { path: 'edit/:contentTypeID', element: <ContentTypeEdit /> },
          ],
        },
        {
          path: 'category',
          children: [
            { element: <Navigate to="/category/list" replace />, index: true },
            { path: 'list', element: <CategoryList /> },
            { path: 'create', element: <CategoryCreate /> },
            { path: 'edit/:categoryID', element: <CategoryEdit /> },
          ],
        },
        {
          path: 'package',
          children: [
            { element: <Navigate to="/package/list" replace />, index: true },
            { path: 'list', element: <PackageList /> },
            { path: 'create', element: <PackageCreate /> },
            { path: 'edit/:packageID', element: <PackageEdit /> },
          ],
        },
        {
          path: 'class',
          children: [
            { element: <Navigate to="/class/list" replace />, index: true },
            { path: 'list', element: <PageOne /> },
            { path: 'my-courses', element: <PageOne /> },
            { path: 'saved-courses', element: <PageOne /> },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// auth
const Login = Loadable(lazy(() => import('../pages/auth/Login')));

// Dashboard
const Home = Loadable(lazy(() => import('../pages/Home')));

// courses
const CoursesList = Loadable(lazy(() => import('../pages/Courses/list')));
const CreateCourse = Loadable(lazy(() => import('../pages/Courses/Create')));

const ParticipantsList = Loadable(lazy(() => import('../pages/Courses/Participants')));
const LeaderBoardList = Loadable(lazy(() => import('../pages/Courses/LeaderBoard')));

// TAGS
const TagsList = Loadable(lazy(() => import('../pages/Tag/list')));
const TagsCreate = Loadable(lazy(() => import('../pages/Tag/create')));
const TagsEdit = Loadable(lazy(() => import('../pages/Tag/edit')));
// ZONE
const ZoneList = Loadable(lazy(() => import('../pages/CourseLevel/list')));
const ZoneCreate = Loadable(lazy(() => import('../pages/CourseLevel/create')));
const ZoneEdit = Loadable(lazy(() => import('../pages/CourseLevel/edit')));
// TRAINER
const TrainerList = Loadable(lazy(() => import('../pages/Trainer/list')));
const TrainerCreate = Loadable(lazy(() => import('../pages/Trainer/create')));
const TrainerEdit = Loadable(lazy(() => import('../pages/Trainer/edit')));
// FAQ
const FaqList = Loadable(lazy(() => import('../pages/Faq/list')));
const FaqCreate = Loadable(lazy(() => import('../pages/Faq/create')));
const FaqEdit = Loadable(lazy(() => import('../pages/Faq/edit')));
// Story
const StoryList = Loadable(lazy(() => import('../pages/Story/list')));
const StoryCreate = Loadable(lazy(() => import('../pages/Story/create')));
const StoryEdit = Loadable(lazy(() => import('../pages/Story/edit')));
// Slider
const SliderList = Loadable(lazy(() => import('../pages/Slider/list')));
const SliderCreate = Loadable(lazy(() => import('../pages/Slider/create')));
const SliderEdit = Loadable(lazy(() => import('../pages/Slider/edit')));
// CONTENT TYPE
const ContentTypeList = Loadable(lazy(() => import('../pages/ContentType/list')));
const ContentTypeCreate = Loadable(lazy(() => import('../pages/ContentType/create')));
const ContentTypeEdit = Loadable(lazy(() => import('../pages/ContentType/edit')));
// CATEGORY
const CategoryList = Loadable(lazy(() => import('../pages/Category/list')));
const CategoryCreate = Loadable(lazy(() => import('../pages/Category/create')));
const CategoryEdit = Loadable(lazy(() => import('../pages/Category/edit')));
// PACKAGE
const PackageList = Loadable(lazy(() => import('../pages/Package/list')));
const PackageCreate = Loadable(lazy(() => import('../pages/Package/create')));
const PackageEdit = Loadable(lazy(() => import('../pages/Package/edit')));

const PageOne = Loadable(lazy(() => import('../pages/PageOne')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
